import { Button } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { adapterName } from '@/components/Integrations/adapters/copy';
import { useIntegrationCapabilities } from '@/components/Integrations/capabilities';
import { CodeBlock } from '@/components/Ui/CodeBlock';
import { CopyButton } from '@/components/Ui/CopyButton';
import { DataTable } from '@/components/Ui/DataTable';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Section, SectionBody } from '@/components/Ui/Section';
import { Tag } from '@/components/Ui/Tag';
import { UiLink } from '@/components/Ui/UiLink';
import { API_BASE_URL } from '@/constants';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { mcpConnectCommand, mcpEndpointUrl } from '@/lib/mcp-connect';
import { apiReferenceUrl } from '@/lib/origins';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import { ApiKeysSection } from './ApiKeysSection';

const MCP_URL = mcpEndpointUrl(API_BASE_URL);
const MCP_COMMAND = mcpConnectCommand(MCP_URL);

const REJECTIONS = ['400', '402', '403', '429'] as const;

export function DevelopersScreen() {
  const { t } = useTranslation();
  const caps = useIntegrationCapabilities();
  const [params] = useSearchParams();
  const location = useLocation();
  useScrollToSection(params.get('section') === 'mcp' ? 'mcp' : null, location.key);
  const [hasSso, setHasSso] = useState<boolean | null>(null);

  useEffect(() => {
    let live = true;
    useRustyState
      .getState()
      .api.listIdentities()
      .then((ids) => {
        if (live) setHasSso(ids.some((i) => i.method === 'Oidc'));
      })
      .catch(() => {
        if (live) setHasSso(null);
      });
    return () => {
      live = false;
    };
  }, []);

  const signed = Object.values(caps).filter((c) => c.signed);
  const signedNames = signed.map((c) => adapterName(c.kind, t));
  const signatureHeader = signed.find((c) => c.signature_header)?.signature_header ?? null;
  const idempotencyHeader = signed.find((c) => c.idempotency_header)?.idempotency_header ?? null;

  return (
    <Page width="narrow">
      <PageHeader
        title={t('developers.pageTitle')}
        subtitle={t('developers.subtitle')}
        actions={
          <Button
            component="a"
            href={apiReferenceUrl()}
            target="_blank"
            rel="noreferrer"
            variant="default"
          >
            {t('developers.apiReference')}
          </Button>
        }
      />

      <ApiKeysSection />

      <Section
        id="mcp"
        title={t('developers.mcp.title')}
        actions={
          hasSso === null ? undefined : hasSso ? (
            <Tag variant="ok">{t('developers.mcp.ready')}</Tag>
          ) : (
            <Tag variant="pending">{t('developers.mcp.needsSso')}</Tag>
          )
        }
      >
        <LedgerRow
          label={t('developers.mcp.signIn')}
          value={
            <span>
              {hasSso === false ? t('developers.mcp.ssoMissing') : t('developers.mcp.ssoConnected')}
            </span>
          }
          help={
            <>
              {t('developers.mcp.manageAt')}{' '}
              <UiLink to="/account/security">{t('nav.security')}</UiLink>.
            </>
          }
        />
        <LedgerRow
          label={t('developers.mcp.addToClaude')}
          value={
            <span className={shared.stack}>
              <CodeBlock code={MCP_COMMAND} wrap />
              <span className={shared.help}>{t('developers.mcp.otherAgents')}</span>
            </span>
          }
          action={<CopyButton text={MCP_COMMAND} />}
        />
        <LedgerRow
          label={t('developers.mcp.endpoint')}
          value={<span className={shared.mono}>{MCP_URL}</span>}
          action={<CopyButton text={MCP_URL} />}
        />
        <LedgerRow
          label={t('developers.mcp.canDo')}
          value={
            <span className={shared.stack} style={{ gap: 4 }}>
              <span>{t('developers.mcp.canDoRead')}</span>
              <span>{t('developers.mcp.canDoApproval')}</span>
            </span>
          }
        />
      </Section>

      <Section title={t('developers.signed.title')}>
        <LedgerRow
          label={t('developers.signed.which')}
          value={
            <span>
              {t('developers.signed.whichValue', {
                names: signedNames.join(', '),
              })}
            </span>
          }
        />
        <LedgerRow
          label={t('developers.signed.header')}
          value={
            signatureHeader ? (
              <span className={shared.mono}>{signatureHeader}</span>
            ) : (
              <span className={shared.sub}>–</span>
            )
          }
          help={t('developers.signed.headerHelp')}
        />
        <LedgerRow
          label={t('developers.signed.duplicates')}
          value={
            <span>
              {idempotencyHeader ? (
                <>
                  {t('developers.signed.duplicatesBefore')}{' '}
                  <span className={shared.mono}>{idempotencyHeader}</span>
                  {t('developers.signed.duplicatesAfter')}
                </>
              ) : (
                t('developers.signed.duplicatesNoHeader')
              )}
            </span>
          }
        />
      </Section>

      <Section title={t('developers.rejections.title')}>
        <DataTable
          caption={t('developers.rejections.title')}
          rows={[...REJECTIONS]}
          rowKey={(r) => r}
          columns={[
            {
              key: 'status',
              header: t('developers.rejections.colStatus'),
              render: (r) => <span className={shared.mono}>{r}</span>,
            },
            {
              key: 'reason',
              header: t('developers.rejections.colReason'),
              render: (r) => t(`developers.rejections.reason${r}`),
            },
          ]}
        />
        <SectionBody>
          <p className={shared.help} style={{ margin: 0 }}>
            {t('developers.rejections.apiCaptcha')}
          </p>
        </SectionBody>
      </Section>
    </Page>
  );
}
